import { z } from 'zod';

import { EQUITY_GRANT_TYPES_WITH_EXERCISE_PRICE, MAX_NAME_LENGTH, MAX_NOTES_LENGTH } from './add-account-constants';

const name = z
  .string()
  .trim()
  .min(1, 'Name is required')
  .max(MAX_NAME_LENGTH, `Name must be at most ${MAX_NAME_LENGTH} characters`);

const MONEY_PATTERN = /^\d{1,13}(\.\d{0,2})?$/;
const QUANTITY_PATTERN = /^\d{1,13}(\.\d{0,6})?$/;
const YEARS_PATTERN = /^\d{1,3}(\.\d{0,2})?$/;
const PERCENT_PATTERN = /^\d{1,3}(\.\d{0,4})?$/;

export function isPositiveAmount(value: string) {
  return MONEY_PATTERN.test(value) && Number(value) > 0;
}

export function isNonNegativeAmount(value: string) {
  return MONEY_PATTERN.test(value);
}

export function isPositiveQuantity(value: string) {
  return QUANTITY_PATTERN.test(value) && Number(value) > 0;
}

export function isPositiveYears(value: string) {
  return YEARS_PATTERN.test(value) && Number(value) > 0;
}

export function isNonNegativeYears(value: string) {
  return YEARS_PATTERN.test(value);
}

const money = z.string().trim().min(1, 'Enter an amount');

const positiveMoney = money.refine(isPositiveAmount, 'Enter an amount greater than $0');
const nonNegativeMoney = money.refine(isNonNegativeAmount, 'Enter an amount of $0 or more');

const notes = z.string().trim().max(MAX_NOTES_LENGTH, `Notes must be at most ${MAX_NOTES_LENGTH} characters`);

export const accountFormSchema = z.object({
  name,
  subtype: z.string().min(1, 'Type is required'),
  balance: nonNegativeMoney,
});

export const assetFormSchema = z.object({
  description: name,
  assetClass: z.string().min(1, 'Type is required'),
  assetSubclass: z.string(),
  currentValue: positiveMoney,
  notes,
});

export const liabilityFormSchema = z.object({
  name,
  providerName: z.string().trim().max(MAX_NAME_LENGTH, `Lender must be at most ${MAX_NAME_LENGTH} characters`),
  type: z.string().min(1, 'Type is required'),
  currentBalance: nonNegativeMoney,
  originalBalance: positiveMoney,
  acquiredAt: z
    .date({ required_error: 'Start date is required', invalid_type_error: 'Start date is required' })
    .max(new Date(), "Start date can't be in the future"),
  annualInterestPercent: z
    .string()
    .trim()
    .refine(
      (value) => value === '' || (PERCENT_PATTERN.test(value) && Number(value) <= 100),
      'Enter a percentage between 0 and 100',
    ),
});

export const companyEquityFormSchema = z
  .object({
    companyEquityCompanyName: z
      .string()
      .trim()
      .min(1, 'Company name is required')
      .max(MAX_NAME_LENGTH, `Company name must be at most ${MAX_NAME_LENGTH} characters`),
    companyEquityCompanyTicker: z
      .string()
      .trim()
      .min(1, 'Company ticker is required')
      .max(10, 'Enter a shorter ticker'),
    companyEquityCurrentPrice: positiveMoney,
    addGrantDetails: z.boolean(),
    companyEquityGrantType: z.string(),
    companyEquitySharesIssued: z.string(),
    companyEquityExercisePrice: z.string(),
    companyEquityVestingStartDate: z.date().optional(),
    companyEquityVestingDuration: z.string(),
    companyEquityVestingCliff: z.string(),
    companyEquityVestingRate: z.string(),
    currentValue: positiveMoney,
  })
  .superRefine((values, ctx) => {
    if (!values.addGrantDetails) {
      return;
    }
    if (values.companyEquityGrantType === '') {
      ctx.addIssue({ code: 'custom', path: ['companyEquityGrantType'], message: 'Grant type is required' });
    }
    if (!isPositiveQuantity(values.companyEquitySharesIssued)) {
      ctx.addIssue({
        code: 'custom',
        path: ['companyEquitySharesIssued'],
        message: 'Enter a number of shares greater than zero',
      });
    }
    if (
      EQUITY_GRANT_TYPES_WITH_EXERCISE_PRICE.includes(values.companyEquityGrantType) &&
      !isPositiveAmount(values.companyEquityExercisePrice)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['companyEquityExercisePrice'],
        message: 'Enter an exercise price greater than $0',
      });
    }
    if (values.companyEquityVestingStartDate === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['companyEquityVestingStartDate'],
        message: 'Vesting start date is required',
      });
    } else if (values.companyEquityVestingStartDate > new Date()) {
      ctx.addIssue({
        code: 'custom',
        path: ['companyEquityVestingStartDate'],
        message: "Vesting start date can't be in the future",
      });
    }
    if (!isPositiveYears(values.companyEquityVestingDuration)) {
      ctx.addIssue({
        code: 'custom',
        path: ['companyEquityVestingDuration'],
        message: 'Enter a vesting duration greater than zero',
      });
    }
    if (!isNonNegativeYears(values.companyEquityVestingCliff)) {
      ctx.addIssue({ code: 'custom', path: ['companyEquityVestingCliff'], message: 'Enter a cliff of zero or more' });
    }
    if (values.companyEquityVestingRate === '') {
      ctx.addIssue({ code: 'custom', path: ['companyEquityVestingRate'], message: 'Vesting rate is required' });
    }
  });

export type CompanyEquityFormValues = z.infer<typeof companyEquityFormSchema>;

export type AccountFormValues = z.infer<typeof accountFormSchema>;
export type AssetFormValues = z.infer<typeof assetFormSchema>;
export type LiabilityFormValues = z.infer<typeof liabilityFormSchema>;
